import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/pjr/organisms/Meta';
import { Terms } from '@bizapp-lp/pjr/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    note: {
      margin: '48px 0 0 0 !important',
    },
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
    noteList: {
      margin: '8px 0 0 !important',
      padding: '0 !important',
      '& li': {
        position: 'static',
        listStyle: 'none',
        marginTop: '-13px !important',
        marginBottom: '0 !important',
        '&:before': {
          content: "'※'",
          position: 'relative',
          top: '21px',
          left: '-14px',
          fontSize: '14px',
          lineSpacing: '21px',
        },
      },
    },
    lineBreak: {
      '& span': {
        display: 'block',
      },
    },
  }),
);

const title = 'Project Roomサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Project Roomサービス仕様書です。';

const ServiceSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>Project Roomサービス仕様書</span>
      </>
    ),
    headerContent: '',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Project Room（以下、Project
            Room）利用にあたり株式会社ワークスアプリケーションズ（以下、弊社）より提供可能なクラウドサービスの内容を記述したものです。Project
            Roomの利用に際しては、本内容および「HUE Works Suite / HUE Works
            Suite DX Solutions利用規約」に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と利用規約の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">2. Project Roomサービス概要</Typography>
          <Typography variant="body1">
            Project
            Roomは法人向けプロジェクトコミュニケーションサービスです。コミュニケーションサービスでよく利用される基本機能に加えファイル管理機能を搭載し、チームやプロジェクトでのコラボレーションによるプロジェクト推進の効率化をサポートします。
          </Typography>
          <Typography variant="body1">
            ※Project
            Roomは法人のお客様向けサービスです。ご利用は法人（公的機関を含む）に限らせていただきます。
          </Typography>
          <Typography variant="body1">
            ※Project
            Roomでは外部ユーザーをゲストユーザーとして招待し、共同で利用することが可能です。ただし、当該外部ユーザーによるProject
            Room利用に関連する一切の行為はお客様による行為とみなすこととし、当該外部ユーザーによる利用規約および本仕様書の違反についてはお客様による違反としてお客様が弊社に対して一切の責任を負うものとします。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Project
              Roomでは下記機能を提供いたします。あわせて、初期構築作業（利用環境構築作業および管理者アカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>アカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ゲストアカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ライセンス管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>セキュリティ管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>利用状況モニター</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>監査ログビューアー</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>
                      プロジェクト（プロジェクトに関するやりとりをプロジェクトメンバーと実施するためのスペース）作成
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      各プロジェクトのへ招待可能なゲストを制限するためのメールドメインによる許可リスト設定
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      コミュニケーション（フィード投稿、コメント、いいね）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ファイル管理（ファイルアップロード、プレビュー、ダウンロード、フォルダ管理）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>カレンダー（予定登録）</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ※ゲストユーザーは招待されたプロジェクト内の機能のみ利用できます。権限管理にて管理者権限を付与することや、プロジェクトの管理者になることはできません。
                    </ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.2 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと管理者アカウントの初期ログインIDをお送りいたします。2ユーザー目以降については、管理者アカウントにてお客様ご自身で登録いただけます。
            </Typography>
            <Typography variant="body1">
              ※トライアルサービスを利用されている場合は、トライアルサービス利用にあたり作成したアカウントおよびデータ（以下、データ等）を継続して利用することが可能です。継続利用をご希望の場合には、データ等移行作業のため1日程度環境停止時間をいただきます。トライアルサービスからデータ等を引き継がずに本サービスを利用される場合には、停止時間は伴いません。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.3 サポートサービス</Typography>
            <Typography variant="body1">
              お申し込みいただきましたメールアドレスあてに機能マニュアル、管理者マニュアルおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。3営業日をめどに１次回答いたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.4 利用料金</Typography>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【標準サービス】</Typography>
              <Typography variant="body1">[月額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    月額利用料：1,300円(税抜）/ライセンス
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        初回月額利用料に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <Typography variant="body1">[年額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    年額利用料：13,000円（税抜）/ライセンス
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        月額利用料の2ヶ月分を割り引いた金額になります。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        初回年額利用料に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <List component="ul" className={classes.note}>
                <ListItem>
                  <ListItemText>
                    「契約ライセンス数×10GB」の容量制限があります。容量制限は全ユーザーで利用できる総量となります。
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    Project
                    Roomライセンス数は、一般ユーザーのアカウント（以下、一般アカウント）数、及び、ゲストユーザーのアカウント（以下、ゲストアカウント）数の総数とします。
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    利用料金は、契約ライセンス数に対して発生します。ライセンスの付与状況やライセンス付与したアカウントの状態（有効/無効）は影響しません。
                  </ListItemText>
                </ListItem>
              </List>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ライセンス</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Project Roomライセンス
                      </TableCell>
                      <TableCell>
                        Project
                        Room機能を利用するアカウントに付与するライセンスです。同時にライセンスを付与するアカウント数が契約ライセンス数を超えない限り、ライセンスを付与するアカウントの変更は同一月内に何度でも行うことができます。
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body1">
                各種管理者は、ライセンスの付与を必要とせず作成することができます。
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>権限（ロール）</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        特権管理者
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          すべての管理者機能を利用できる特権ロールです。
                        </span>
                        <span style={{ display: 'block' }}>
                          特権管理者やアカウント管理者の追加/編集/停止、アプリケーションのライセンス付与をすることができます。
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        アカウント管理者
                      </TableCell>
                      <TableCell>
                        一般アカウント、ゲストアカウントの追加/編集/停止およびアカウント管理者の追加/編集/停止、利用アプリケーションのライセンス付与をできる権限です。特権管理者の追加/編集/停止はできません。
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        セキュリティ管理者
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          セキュリティの設定に特化した管理者です。
                        </span>
                        <span style={{ display: 'block' }}>
                          セキュリティポータルで、IPアクセス制御、シングルサインオン、2段階認証の設定を行うことができます。
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Project Room管理者
                      </TableCell>
                      <TableCell>
                        Project Roomの使用状況をモニターすることができます。
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Typography variant="body1">
                  【例】100ライセンス分の利用契約を締結しており、Project
                  Roomライセンスを付与したアカウントが95、次月から利用開始予定のライセンス未付与のアカウントが10、登録されている。特権管理者、アカウント管理者それぞれ1アカウントずつ登録されており、いずれもライセンスを付与していない。
                </Typography>
                <Typography variant="body1">
                  →登録アカウント数は、95（Project Roomユーザーのアカウント）+
                  10（次月利用開始予定アカウント）+ 1（特権管理者のアカウント）+
                  １（アカウント管理者のアカウント）= 107
                </Typography>
                <Typography variant="body1">
                  付与ライセンス数は、95（Project
                  Roomユーザー）となり、付与ライセンス数（95）が契約ライセンス数（100）を超えないため、契約ライセンス数である100ライセンス分の利用料金が発生します。
                </Typography>
                <Typography variant="body1">
                  この時点においてはProject
                  Roomユーザーの付与ライセンス数が95のため契約ライセンス数の範囲内に収まっていますが、次月から予定どおり10アカウント分についてProject
                  Roomユーザーとしてライセンスの付与が行われた場合、付与ライセンス数は105となり、契約ライセンス数（100）を超えるため、少なくとも超過する5ライセンス分の利用契約を、予め追加で締結いただく必要があります。
                </Typography>
              </TableContainer>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 算定方法およびお支払い</Typography>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">算定方法</Typography>
              <Typography variant="body1">[月額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    利用規約に定める本サービス利用開始日から、利用料金が発生します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    毎月末時点の契約ライセンス数に応じて当月の利用料金を確定します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    本サービス利用開始日が月の途中の場合、当該月の利用料金は日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        当該月の利用料金 ＝｛月額利用料－（月額利用料 ×
                        利用しない日数 ÷ 暦日数）｝ × 契約ライセンス数
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        ※当該月の初日から本サービス利用開始日の前日までの日数を「利用しない日数」とします。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            1月16日に、100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                1月16日～1月31日（16日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛1,300円－（1,300円×15日÷31日）｝×100ライセンス
                              </ListItemText>
                              <ListItemText>＝671円×100ライセンス</ListItemText>
                              <ListItemText>＝67,100円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    月の途中でライセンス数を追加する場合、当該追加ライセンス数分の利用料金は、日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        追加ライセンス数分の利用料金
                        ＝｛月額利用料－（月額利用料 × 利用しない日数 ÷
                        暦日数）｝ × 追加ライセンス数
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            4月1日から4月30日までの月額契約期間中の4月16日に、追加で100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                4月16日～4月30日（15日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛1,300円－（1,300円×15日÷30日）｝×100ライセンス
                              </ListItemText>
                              <ListItemText>＝650円×100ライセンス</ListItemText>
                              <ListItemText>＝65,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    日割り計算は先乗後除で行い、小数点は四捨五入とします
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間の途中で利用契約が終了する場合における利用料金は、日割り計算による減額又は返金は行わず、利用契約の終了日から当該利用契約が終了しなかった場合における契約期間満了日までの利用料金も発生します
                  </ListItemText>
                </ListItem>
              </List>
              <Typography variant="body1">[年額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    利用規約に定める本サービス利用開始日から、利用料金が発生します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間満了日が属する月（以下、契約期間満了月）の前月末時点の契約ライセンス数に応じて翌契約期間の利用料金を確定します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    本サービス利用開始日が月の途中の場合、初年度の利用料金は、当該月の利用料金（月額利用料を日割り計算した金額（先乗後除、小数点は四捨五入））に2か月分の割引率を乗じた金額（小数点は四捨五入）を、年額利用料に加算した金額となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            1月16日に、100ライセンスの利用を開始する場合（初年度の契約期間は1月16日から翌年1月末日までとなり、次年度以降の契約期間は2月1日から翌年1月末日までとなります）
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ①1月16日～1月31日（16日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛月額利用料－(月額利用料×利用しない日数÷暦日数）｝×2か月分の割引率×契約ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝｛1,300円－（1,300円×15日÷31日）｝×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>
                                ＝671円×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>＝559円×100ライセンス</ListItemText>
                              <ListItemText>＝55,900円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ②2月1日～翌年1月31日（12か月分）
                              </ListItemText>
                              <ListItemText>
                                ＝年額利用料×契約ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝13,000円×100ライセンス
                              </ListItemText>
                              <ListItemText>＝1,300,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                初年度の利用料金＝①＋②＝1,355,900円（税抜）（本サービス利用開始日が月の途中なので、支払期日は翌月2月末日）
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    本サービス利用開始日後にライセンス数を追加する場合、当該追加ライセンス数分の初回利用料金は、当該追加月の利用料金（月の途中で追加した場合は月額利用料を日割り計算した金額（先乗後除、小数点は四捨五入））に2か月分の割引率を乗じた金額（小数点は四捨五入）を、月額利用料に当該追加月の翌月から契約期間満了月まで月数を乗じて2か月分の割引率を乗じた金額（小数点は四捨五入）に加算した金額を一括でお支払いいただきます
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            [追加月が契約期間満了月以外の場合]
                          </ListItemText>
                          <ListItemText>
                            1月1日から12月31日までの年額契約期間中の4月16日に、追加で100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ①4月16日～4月30日（15日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛月額利用料－(月額利用料×利用しない日数÷暦日数）｝×2か月分の割引率×追加ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝｛1,300円－（1,300円×15日÷30日）｝×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>
                                ＝650円×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>＝542円×100ライセンス</ListItemText>
                              <ListItemText>＝54,200円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ②5月1日～12月31日（8か月分）
                              </ListItemText>
                              <ListItemText>
                                ＝（月額利用料×利用月数）×2か月分の割引率×追加ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝8,667円×100ライセンス
                              </ListItemText>
                              <ListItemText>＝866,700円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                追加ライセンス数分の利用料金＝①＋②＝920,900円（税抜）（支払期日は翌月5月末日）
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            [追加月が契約期間満了月に該当する場合]
                          </ListItemText>
                          <ListItemText>
                            1月1日から12月31日までの年額契約期間中の12月16日に、追加で100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ①12月16日～12月31日（16日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛月額利用料－(月額利用料×利用しない日数÷暦日数）｝×2か月分の割引率×追加ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝｛1,300円－（1,300円×15日÷31日）｝×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>
                                ＝671円×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>＝559円×100ライセンス</ListItemText>
                              <ListItemText>＝55,900円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ②翌年1月1日～12月31日（12か月分）
                              </ListItemText>
                              <ListItemText>
                                ＝年額利用料×契約ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝13,000円×100ライセンス
                              </ListItemText>
                              <ListItemText>＝1,300,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                追加ライセンス数分の初回利用料金＝①＋②＝1,355,
                                900円（税抜）（支払期日は翌月にあたる翌年1月末日）
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間の途中で利用契約が終了する場合における利用料金は、日割り計算による減額又は返金は行わず、利用契約の終了日から当該利用契約が終了しなかった場合における契約期間満了日までの利用料金も発生します
                  </ListItemText>
                </ListItem>
              </List>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">お支払い</Typography>
              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【請求書支払】</Typography>
                <Typography variant="body1">[月額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      毎月末締め、翌月末払い（後払い）
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">[年額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      契約期間満了月の前月末締め、契約期間満了月の月末に次年度分をお支払い（前払い）
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              契約期間が1月1日から12月31日までの場合、契約期間満了月の前月にあたる11月末日を毎年締め日として請求額が決定し、契約期間満了月にあたる12月末日までに次年度分をお支払いいただきます。
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      初年度分の支払期日は、本サービス利用開始日が月の初日の場合は当月末日、月の途中の場合は翌月末日となります
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              本サービス利用開始日が2022年1月1日の場合、初年度の契約期間は2022年1月1日から2022年12月31日までとなり、2021年12月31日を締め日として利用料金を確定し、支払期日は2022年1月末日となります。
                            </ListItemText>
                          </ListItem>
                          <ListItem>
                            <ListItemText>
                              本サービス利用開始日が2022年1月16日の場合、初年度の契約期間は2022年1月16日から2023年1月31日までとなり、2022年1月15日を締め日として利用料金を確定し、支払期日は2022年2月末日となります。
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      追加ライセンス数分の初回利用料金の支払期日は、当該追加月の翌月末日となります。次回以降は、既存ライセンス数と合算して契約期間満了月の前月末締め、契約期間満了月の月末に次年度分をお支払い（前払い）となります
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              [追加月が契約期間満了月以外の場合]
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  契約期間が1月1日から12月31日までで既に500ライセンスを利用している場合、2022年10月10日に200ライセンスを追加すると、追加する200ライセンス分の初回利用料金（2022年10月分の日割り＋2022年11月～12月分）の支払期日は2022年11月末日となり、次回2023年度分のお支払いは、既存500ライセンスの2023年度分のお支払いと合算して2022年12月末日に年額利用料金をお支払いいただきます。
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                          <ListItem>
                            <ListItemText>
                              [追加月が契約期間満了月に該当する場合]
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  契約期間が1月1日から12月31日までで既に500ライセンスを利用している場合、2022年12月10日に200ライセンスを追加すると、追加する200ライセンス分の初回利用料金（2022年12月分の日割り＋2023年1月～12月分（2023年度分））の支払期日は2023年1月末日となり、次回2024年度分のお支払いは、既存500ライセンスの2024年度分のお支払いと合算して2023年12月末日に年額利用料金をお支払いいただきます。
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  ※請求はマネーフォワードケッサイ株式会社による「マネーフォワード
                  ケッサイ」サービスにて行われます
                </Typography>
              </Container>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.6 提供条件</Typography>
            <Typography variant="body1">
              Project Roomの提供に際しては、
              カレンダー機能内のZoom連携において、Zoom Video Communications,
              Inc.（以下「Zoom」といいます。）が提供するサービスが利用されます。お客様は、Zoom連携を利用するにあたり、Zoom所定の「ZOOM
              TERMS OF
              SERVICE」（その名称又は内容に変更が生じた場合には、当該変更後の名称及び内容によるものとします。
              なお、最新版は
              <Link href="https://zoom.us/terms" target="_blank">
                https://zoom.us/terms
              </Link>
              に掲載されています。）のうちEnd
              Userが遵守すべき事項について同意し、これを遵守するものとします。
            </Typography>
          </Container>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">3. 商標について</Typography>
          <Typography variant="body1">
            本書に掲載されている各社名あるいは各製品名は、各社の登録商標または商標です。
          </Typography>
          <List component="ul">
            <ListItem>
              <ListItemText>
                Zoomは、Zoom Video Communications,
                Inc.の米国および日本を含むその他の国における商標または登録商標です。
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                マネーフォワードケッサイは、マネーフォワードケッサイ株式会社の登録商標または商標です。
              </ListItemText>
            </ListItem>
          </List>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">付則</Typography>
          <Typography>
            改正後の本仕様書は、2023年4月1日より効力を有するものとします。
          </Typography>
          <Typography className={classes.lineBreak}>
            <span>初版施行日：2021年7月7日</span>
            <span>第１回改正施行日：2021年7月12日</span>
            <span>
              <Link
                component={RouterLink}
                to="/pjr/spec-archive/service-spec-02-20211015"
              >
                第２回改正施行日：2021年10月15日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/pjr/spec-archive/service-spec-03-20220328"
              >
                第３回改正施行日：2022年3月28日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/pjr/spec-archive/service-spec-04-20220627"
              >
                第４回改正施行日：2022年6月27日
              </Link>
            </span>
            <span>第５回改正施行日：2023年4月1日</span>
          </Typography>
        </Container>
      </Terms>
    </>
  );
};

export default ServiceSpec;
